// Copyright 2025 Sorama B.V.

import { FileToUpload } from "controls/file-dropbox/FileDropbox";
import { SorXMetadataFull } from "datatypes/SorXFormat";
import { PrpdBinData } from "services/ReportEx/PrpdDataService";

const FEATURE_IMAGE_COUNT = 3;

export const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error);
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
    });
};

export async function parseSorXFile(
    file: FileToUpload,
    parsePrpdBin?: (buffer: ArrayBuffer) => PrpdBinData,
    options: { featureImageCount?: number } = {}
): Promise<SorXMetadataFull> {
    const featureImageCount = options.featureImageCount ?? FEATURE_IMAGE_COUNT;
    const arrayBuffer = await file.data.arrayBuffer();
    const JSZip = await import("jszip");
    const sorX = await JSZip.loadAsync(arrayBuffer);
    const metadataFile = sorX.file("metadata.json");
    if (!metadataFile) throw new Error("SorX file missing required metadata.json");

    let metadataRaw: unknown;
    try {
        metadataRaw = JSON.parse(await metadataFile.async("string"));
    } catch (err) {
        throw new Error("Failed to parse metadata.json: " + (err as Error).message);
    }

    const imagePromise = (async () => {
        // cam-iv64-ex uses "thumbnail.jpeg"; older sor files use "image.jpeg"
        const imageEntry = sorX.file("thumbnail.jpeg") ?? sorX.file("image.jpeg");
        if (!imageEntry) return undefined;
        const blob = new Blob([await imageEntry.async("blob")], { type: "image/jpeg" });
        return await blobToBase64(blob);
    })();

    const prpdPromise = (async () => {
        const prpdEntry = sorX.file("PrpdData.bin");
        if (!prpdEntry || !parsePrpdBin) return undefined;
        try {
            const prpdArrayBuffer = await prpdEntry.async("arraybuffer");
            return parsePrpdBin(prpdArrayBuffer);
        } catch {
            return undefined;
        }
    })();

    const rawAudioEncPromise = (async () => {
        const entry = sorX.file("raw_audio.ibin.enc");
        if (!entry) return undefined;
        return new Blob([await entry.async("arraybuffer")], { type: "application/octet-stream" });
    })();

    const featureImagesPromise = (async () => {
        const images: string[] = [];
        const tasks: Promise<void>[] = [];
        for (let i = 1; i <= featureImageCount; i++) {
            const entry = sorX.file(`notes/${i}.jpeg`);
            if (!entry) continue;
            tasks.push(
                (async () => {
                    try {
                        const blob = new Blob([await entry.async("blob")], { type: "image/jpeg" });
                        images.push(await blobToBase64(blob));
                    } catch {
                        // ignore individual image errors
                    }
                })()
            );
        }
        await Promise.all(tasks);
        return images;
    })();

    const [imageBase64, prpdBinData, rawAudioEncryptionBlob, featureImages] = await Promise.all([
        imagePromise,
        prpdPromise,
        rawAudioEncPromise,
        featureImagesPromise,
    ]);

    const result: SorXMetadataFull = {
        ...(metadataRaw as object),
        image: imageBase64 ?? "",
        featureImages: featureImages ?? [],
        prpdBinData: prpdBinData,
        rawAudioEncryptionBlob: rawAudioEncryptionBlob,
    } as SorXMetadataFull;
    return result;
}

export const SorxHelpers = { blobToBase64, parseSorXFile };
